import KeyTakeaway from "@/components/ui/key-takeaway";

export default function Conclusion() {
  return (
    <section id="conclusion" className="mb-16 scroll-mt-20">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
        Should You Use Peptides? The Informed Decision
      </h2>
      
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="p-6">
          <p className="mb-6">
            After exploring the science, history, applications, and current landscape of peptides, 
            we arrive at the fundamental question: <em>Should you use peptides?</em> The answer isn't 
            universal—it depends entirely on your individual circumstances, health objectives, 
            and approach to making medical decisions.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-4">Personal Risk-Benefit Assessment</h3>
              <p className="mb-4">
                The decision to use peptides should be based on a thorough risk-benefit analysis tailored 
                to your specific situation. Key considerations include:
              </p>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li><strong>Your health condition</strong> and whether conventional treatments have been effective</li>
                <li><strong>Risk tolerance</strong> - comfort with experimental treatments with varying levels of evidence</li>
                <li><strong>Potential benefits</strong> versus known and unknown risks</li>
                <li><strong>Quality of available products</strong> and your ability to source reliable options</li>
                <li><strong>Access to medical supervision</strong> from knowledgeable practitioners</li>
              </ul>
              <p>
                Someone with a serious, treatment-resistant condition might reasonably accept higher risks 
                than someone who's generally healthy but interested in optimization or prevention.
              </p>
            </div>
            
            <div>
              <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-4">Understanding the Science is Non-Negotiable</h3>
              <p className="mb-4">
                If you're considering peptides, understanding cellular mechanisms and signaling pathways is 
                <strong> mandatory</strong>, not optional. Without this knowledge, you cannot:
              </p>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>Evaluate the legitimacy of claims about specific peptides</li>
                <li>Understand potential cross-effects with other medications or supplements</li>
                <li>Assess whether reported side effects make mechanistic sense</li>
                <li>Adapt protocols based on your body's responses</li>
              </ul>
              <p>
                No "peptide cheat sheet" or generic protocol will serve you for long. The field evolves rapidly, 
                and your body's specific responses need personalized consideration.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-[#EBF5FB] to-white p-5 rounded-lg mb-8">
            <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">Beyond the Information Gap</h3>
            <p className="mb-4">
              The current state of peptide information presents serious challenges:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded shadow-sm">
                <h4 className="font-bold text-[#E74C3C] mb-2">Academic Research</h4>
                <p className="text-sm">
                  Rigorous but often inaccessible to non-scientists, focused on mechanisms rather than practical applications
                </p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <h4 className="font-bold text-[#E74C3C] mb-2">Clinical Guidance</h4>
                <p className="text-sm">
                  Limited because many peptides lack formal approval, leaving physicians without standard protocols
                </p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <h4 className="font-bold text-[#E74C3C] mb-2">Online Communities</h4>
                <p className="text-sm">
                  Often based on anecdotal evidence, variable quality information, and simplistic protocols
                </p>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            The decision to use peptides is inherently personal. If you choose to explore peptides, 
            do so with comprehensive education, reliable resources, thoughtful integration with your overall 
            health strategy, and preferably under informed medical supervision.
          </KeyTakeaway>
        </div>
      </div>
      
      {/* Next Generation Membership */}
      <div className="bg-gradient-to-r from-[#2C3E50] to-[#1A5276] text-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
            <div className="md:w-2/3">
              <h3 className="font-serif text-2xl font-bold mb-4">Next Generation Membership</h3>
              <p className="mb-4">
                For those seeking to navigate the complex world of peptides with confidence and cutting-edge information, 
                I've created a comprehensive educational experience that goes beyond basic articles and forum discussions.
              </p>
              <p>
                The Next Generation Membership provides the education, community, and tools needed to make truly 
                informed decisions about peptides and other advanced health interventions.
              </p>
            </div>
            <div className="md:w-1/3 flex justify-center">
              <div className="bg-white text-[#2C3E50] rounded-lg p-5 text-center max-w-xs">
                <div className="text-3xl font-bold mb-2">Join Now</div>
                <p className="text-sm mb-3">First 100 members receive exclusive benefits</p>
                <button className="bg-[#3498DB] hover:bg-[#2980B9] text-white py-2 px-6 rounded-full font-semibold transition-colors">
                  Learn More
                </button>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/10 rounded-lg p-5">
              <h4 className="font-bold text-xl mb-3">Membership Benefits</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Weekly interactive deep dives</strong> on peptides and other longevity topics</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Members-only WhatsApp community</strong> for real-time discussion and support</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Monthly Q&A sessions</strong> to address your specific questions</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>20% discount</strong> on select trusted products</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/10 rounded-lg p-5">
              <h4 className="font-bold text-xl mb-3">Exclusive for First 100 Members</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span><strong>Biannual non-medical reviews</strong> of your diagnostic data</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span><strong>Early access to proprietary analysis tools</strong> for wearable data, VO2max, and DEXA scans</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span><strong>Priority access to new content</strong> and special events</span>
                </li>
              </ul>
              
              <div className="mt-4 p-3 bg-white/20 rounded">
                <p className="text-sm italic">
                  <strong>Note:</strong> Diagnostic data reviews are for informational purposes only and do not constitute 
                  medical advice. They are designed to help you better understand your health metrics and identify 
                  potential areas for further investigation with your healthcare provider.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h4 className="font-bold text-xl mb-4">AI-Powered Knowledge Base</h4>
            <p className="mb-4">
              Our curriculum is continuously updated using a proprietary AI-powered knowledge base that digests 
              the latest research in real-time. This means you're always getting the most current, evidence-based 
              information—not outdated protocols or anecdotal advice.
            </p>
            <div className="bg-white/10 p-4 rounded">
              <p className="text-sm font-semibold mb-2">The Next Generation Knowledge System includes:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-[#2980B9] p-3 rounded">
                  <p className="font-semibold text-center">Fundamental Science</p>
                </div>
                <div className="bg-[#2980B9] p-3 rounded">
                  <p className="font-semibold text-center">Clinical Applications</p>
                </div>
                <div className="bg-[#2980B9] p-3 rounded">
                  <p className="font-semibold text-center">Business Applications</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Comparison Chart */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">How We Compare</h3>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#ECF0F1]">
                  <th className="p-3 text-left border">Features</th>
                  <th className="p-3 text-center border"><span className="font-bold text-[#2C3E50]">Next Generation Membership</span></th>
                  <th className="p-3 text-center border">Typical Health Influencer Communities</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border font-semibold">Communication Platform</td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Convenient WhatsApp Community
                    </span>
                  </td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Fragmented Forums & Platforms
                    </span>
                  </td>
                </tr>
                <tr className="bg-[#F8F9FA]">
                  <td className="p-3 border font-semibold">Content Focus</td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Comprehensive (Science + Clinical + Business)
                    </span>
                  </td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Limited to Clinical Applications Only
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="p-3 border font-semibold">Data Analysis</td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Personalized Data Reviews & Custom Tools
                    </span>
                  </td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Generic Guidelines Only
                    </span>
                  </td>
                </tr>
                <tr className="bg-[#F8F9FA]">
                  <td className="p-3 border font-semibold">Research Integration</td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      AI-Powered Research Knowledge Base
                    </span>
                  </td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Sporadic Manual Updates
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="p-3 border font-semibold">Target Audience</td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Clinicians, Entrepreneurs, Consumers
                    </span>
                  </td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Consumers Only
                    </span>
                  </td>
                </tr>
                <tr className="bg-[#F8F9FA]">
                  <td className="p-3 border font-semibold">Product Development</td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Custom-Built Tools & Products
                    </span>
                  </td>
                  <td className="p-3 border text-center">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Third-Party Affiliate Products Only
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-8 text-center">
            <a href="#" className="inline-block bg-gradient-to-r from-[#3498DB] to-[#2980B9] text-white py-3 px-8 rounded-full font-bold text-lg hover:from-[#2980B9] hover:to-[#1A5276] transition-all">
              Become a Founding Member Today
            </a>
            <p className="mt-3 text-[#7F8C8D] italic">
              Limited spots available for exclusive founding member benefits
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}